import {
  AfterViewInit,
  Component,
  inject,
  Inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IonCol, IonRow } from '@ionic/angular/standalone';
import { Subject, take } from 'rxjs';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { SharedModule } from 'src/app/components/shared/shared.module';

import { SpecialCharacters } from 'src/app/core/enums';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CvisSwap } from 'src/app/SCI/core/models/cvis-swap.model';
import { EdiSwap } from 'src/app/SCI/core/models/edi-swap.model';
import {
  ACTIVE_DROPDOWN_OPTIONS,
  AVAILABILITIES,
  CONSTANTS,
  DESTINATION_SORT_OPTIONS,
  LCDS,
  SERVICE_LEVEL_OPTIONS,
  SWAP_LOC_OPTIONS,
  SwapData,
} from './add-edit-swap.constants';

@Component({
  selector: 'app-add-edit-swap',
  imports: [IonCol, IonRow, SharedModule, ReactiveFormsModule],
  templateUrl: './add-edit-swap.component.html',
  styleUrl: './add-edit-swap.component.scss',
})
export class AddEditSwapComponent implements OnInit, AfterViewInit {
  private userService = inject(UserService);

  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly CONSTANTS = CONSTANTS;
  readonly AVAILABILITIES = AVAILABILITIES;
  readonly SERVICE_OPTIONS = SERVICE_LEVEL_OPTIONS;
  readonly DEST_SORT_OPTIONS = DESTINATION_SORT_OPTIONS;
  readonly SWAP_LOC_OPTIONS = SWAP_LOC_OPTIONS;
  readonly LCDS = LCDS;
  readonly SpecialCharacters = SpecialCharacters;
  readonly SRC = IMAGE_SRC;
  readonly ACTIVE_DROPDOWN_OPTIONS = ACTIVE_DROPDOWN_OPTIONS;

  swapForm!: FormGroup;
  defaultEffectiveDate!: Date;
  defaultExpiryDate!: Date;
  userName!: string;

  destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SwapData,
    private fb: FormBuilder,
    public readonly dialogRef: MatDialogRef<AddEditSwapComponent>,
  ) {}

  ngOnInit() {
    this.defaultEffectiveDate = new Date(Date.now());
    this.defaultExpiryDate = new Date(Date.now());
    this.defaultExpiryDate.setFullYear(
      this.defaultExpiryDate.getFullYear() + 15,
    );
    this.defaultExpiryDate.setMonth(this.defaultExpiryDate.getMonth() + 3);
    this.defaultExpiryDate.setDate(this.defaultExpiryDate.getDay() + 5);

    this.swapForm = this.fb.group({
      name: [this.data.val?.name ?? ''],
      active: [
        this.data.val?.active ?? ACTIVE_DROPDOWN_OPTIONS[0].value,
        Validators.compose([Validators.required]),
      ],
      effective: [
        this.data.val?.effective ?? this.defaultEffectiveDate,
        Validators.compose([Validators.required]),
      ],
      expires: [
        this.data.val?.expires ?? this.defaultExpiryDate,
        Validators.compose([Validators.required]),
      ],
      mon: [
        this.data.val?.mon ?? false,
        Validators.compose([Validators.required]),
      ],
      tue: [
        this.data.val?.tue ?? false,
        Validators.compose([Validators.required]),
      ],
      wed: [
        this.data.val?.wed ?? false,
        Validators.compose([Validators.required]),
      ],
      thu: [
        this.data.val?.thu ?? false,
        Validators.compose([Validators.required]),
      ],
      fri: [
        this.data.val?.fri ?? false,
        Validators.compose([Validators.required]),
      ],
      sat: [
        this.data.val?.sat ?? false,
        Validators.compose([Validators.required]),
      ],
      sun: [
        this.data.val?.sun ?? false,
        Validators.compose([Validators.required]),
      ],
      origCode: [
        this.data.val?.origCode ?? '',
        Validators.compose([Validators.required]),
      ],
      origSort: [
        this.data.val?.origSort ?? '',
        Validators.compose([Validators.required]),
      ],
      destSort: [
        this.data.val?.destSort ?? '',
        Validators.compose([Validators.required]),
      ],
      destSlic: [
        this.data.val?.destSlic ?? '',
        Validators.compose([Validators.required]),
      ],
      swapSlic: [
        this.data.val?.swapSlic ?? '',
        Validators.compose([Validators.required]),
      ],
      swapSort: [
        this.data.val?.swapSort ?? '',
        Validators.compose([Validators.required]),
      ],
      service: [
        this.data.val?.service ?? '',
        Validators.compose([Validators.required]),
      ],
      swapService: [
        this.data.val?.swapService ?? '',
        Validators.compose([Validators.required]),
      ],
      monLcd: [
        this.data.val?.monLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      tueLcd: [
        this.data.val?.tueLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      wedLcd: [
        this.data.val?.wedLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      thuLcd: [
        this.data.val?.thuLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      friLcd: [
        this.data.val?.friLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      satLcd: [
        this.data.val?.satLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
      sunLcd: [
        this.data.val?.sunLcd ?? 0,
        Validators.compose([Validators.required]),
      ],
    });
    if (this.data.val?.id) {
      this.swapForm.addControl('id', this.fb.control(this.data.val?.id));
    }

    if (this.data.isEdi) {
      this.swapForm.addControl(
        'swapPkgLocRole',
        this.fb.control(
          this.data.val?.swapPkgLocRole ?? SWAP_LOC_OPTIONS[0].value,
          Validators.required,
        ),
      );
    }
  }

  ngAfterViewInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(take(1))
      .subscribe((user) => {
        this.swapForm.get('name')?.setValue(user.loginId);
      });
  }

  clickSave() {
    const body: EdiSwap | CvisSwap = this.swapForm.getRawValue();
    //Match to BE if applicable
    if (body?.swapPkgLocRole === 'none') {
      body.swapPkgLocRole = null;
    }
    this.dialogRef.close(body);
  }

  clickExit() {
    this.dialogRef.close();
  }
}
